
import { useState, useEffect, createContext, useContext } from 'react';

interface User {
  id: string;
  email: string;
  user_type: 'master' | 'customer';
  name: string;
  avatar?: string;
  phone?: string;
  specialty?: string[];
  experience?: string;
  rating?: number;
  price?: string;
  description?: string;
  created_at: string;
}

interface AdminUser {
  id: string;
  username: string;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  last_login?: string;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  admin: AdminUser | null;
  login: (email: string, password: string) => Promise<void>;
  adminLogin: (username: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  adminLogout: () => void;
  loading: boolean;
}

interface RegisterData {
  email: string;
  password: string;
  name: string;
  userType: 'master' | 'customer';
  phone?: string;
  specialty?: string[];
  experience?: string;
  price?: string;
  description?: string;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth 必须在 AuthProvider 内使用');
  }
  return context;
};

export const useAuthState = () => {
  const [user, setUser] = useState<User | null>(null);
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 从本地存储恢复用户信息
    const savedUser = localStorage.getItem('auth_user');
    const savedAdmin = localStorage.getItem('auth_admin');
    const adminToken = localStorage.getItem('admin_token');
    
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    
    if (savedAdmin && adminToken) {
      // 驗證管理員令牌
      verifyAdminToken(adminToken);
    }
    
    setLoading(false);
  }, []);

  const verifyAdminToken = async (token: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'verify',
          token,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setAdmin(data.admin);
      } else {
        // 令牌無效，清除本地存儲
        localStorage.removeItem('auth_admin');
        localStorage.removeItem('admin_token');
      }
    } catch (error) {
      console.error('驗證管理員令牌失敗:', error);
      localStorage.removeItem('auth_admin');
      localStorage.removeItem('admin_token');
    }
  };

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const response = await fetch('https://hrpxqsicfpubomffqajj.supabase.co/functions/v1/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'login',
          email,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '登录失败');
      }

      setUser(data.user);
      localStorage.setItem('auth_user', JSON.stringify(data.user));
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const adminLogin = async (username: string, password: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'login',
          username,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '管理員登入失敗');
      }

      setAdmin(data.admin);
      localStorage.setItem('auth_admin', JSON.stringify(data.admin));
      localStorage.setItem('admin_token', data.token);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (registerData: RegisterData) => {
    setLoading(true);
    try {
      const response = await fetch('https://hrpxqsicfpubomffqajj.supabase.co/functions/v1/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'register',
          ...registerData,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '注册失败');
      }

      setUser(data.user);
      localStorage.setItem('auth_user', JSON.stringify(data.user));
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('auth_user');
  };

  const adminLogout = () => {
    setAdmin(null);
    localStorage.removeItem('auth_admin');
    localStorage.removeItem('admin_token');
  };

  return {
    user,
    admin,
    login,
    adminLogin,
    register,
    logout,
    adminLogout,
    loading,
  };
};
